import type { OptionType } from '@withjoy/joykit';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { remove } from 'lodash-es';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useTranslation } from '@shared/core';
import type { CookedRegistry } from '@apps/registry/common/selectors/ProductListSelector';
import type { AdminProductFilter, AdminProductFilterKeys, ProductFilter } from '@apps/registry/common/state/RegistryProducts';
import { useQueryParamHelper } from '@shared/core/queryString';

interface ProductListFiltersControllerProps {
  registries?: Array<CookedRegistry>;
  onFilterChange?: (filters: ProductFilter) => void;
}

export enum GiftOptions {
  EVERYTHING = 'everything',
  STILLNEEDED = 'stillNeeded',
  RESERVEDANDPURCHASED = 'reservedAndPurchased',
  PURCHASED = 'purchased',
  RESERVED = 'reserved',
}

const filterQueryParamKey = 'filter';

export const getCountDownRedirectionUrl = (pathname: string, giftOption: GiftOptions) => {
  return { pathname, search: `${filterQueryParamKey}=${giftOption}` };
};

export const useProductListFiltersController = ({ onFilterChange, registries }: ProductListFiltersControllerProps) => {
  const { t2 } = useTranslation('yourRegistry');
  const { everything, stillNeeded, purchased, reserved, reservedAndPurchased } = t2('filterSectionRegistry');
  const { adminRegistryProductFilterClick } = useAdminRegistryTelemetry();
  const queryParamHelper = useQueryParamHelper();

  const giftOptions: OptionType[] = useMemo(
    () => [
      { value: GiftOptions.EVERYTHING, label: everything },
      { value: GiftOptions.STILLNEEDED, label: stillNeeded },
      { value: GiftOptions.RESERVEDANDPURCHASED, label: reservedAndPurchased },
      { value: GiftOptions.PURCHASED, label: purchased },
      { value: GiftOptions.RESERVED, label: reserved },
    ],
    [everything, purchased, reserved, reservedAndPurchased, stillNeeded],
  );

  const [filters, setFilters] = useState<AdminProductFilter[]>([]);
  const [productValue, setProductValue] = useState<OptionType>(giftOptions[0]);

  useEffect(() => {
    if (filters.length && onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters, onFilterChange]);

  const reduceFilter = useCallback((filters: AdminProductFilter[], key: AdminProductFilterKeys, value: string) => {
    const removed = [...filters];

    remove(removed as AdminProductFilter[], (item: AdminProductFilter) => {
      return item.key === key;
    });

    if (value !== '') {
      removed.push({ key: key, value: value });
    }
    return removed;
  }, []);

  useEffect(() => {
    const filterOption = queryParamHelper.getValueString(filterQueryParamKey) || '';
    const filterType = giftOptions.find(option => option.value === filterOption);
    if (filterType) {
      setProductValue(filterType);
      setFilters((filters: AdminProductFilter[]) => {
        return reduceFilter(filters, 'product', filterType.value);
      });
    }
  }, [giftOptions, queryParamHelper, reduceFilter]);

  const handleFilterByGift = (product: OptionType | null): void => {
    if (product) {
      setProductValue(product);
      setFilters((filters: AdminProductFilter[]) => {
        return reduceFilter(filters, 'product', product.value);
      });
      adminRegistryProductFilterClick(product.value);
    }
  };

  return {
    giftOptions,
    handleFilterByGift,
    productValue,
  };
};
